import { type FileSchedule, MissingFileAction, ScheduleFrequency } from '@today/types'
import { NEW_SCHEDULE, WEEKDAYS } from './form-defaults'
import { CheckboxInput, Choice, type FieldErrors, GroupError, NumberInput, TextInput } from './form-fields'

const FREQUENCY_OPTIONS = [
	{ id: ScheduleFrequency.DAILY, text: 'Every day' },
	{ id: ScheduleFrequency.WEEKLY, text: 'On chosen weekdays' },
	{ id: ScheduleFrequency.MONTHLY, text: 'Once a month' },
]

const MISSING_OPTIONS = [
	{ id: MissingFileAction.ALERT, text: 'Alert' },
	{ id: MissingFileAction.NONE, text: 'Do nothing' },
]

/** A frequency change keeps only the day selector that frequency uses. */
const withFrequency = (schedule: FileSchedule, frequency: ScheduleFrequency): FileSchedule => ({
	...schedule,
	frequency,
	daysOfWeek: frequency === ScheduleFrequency.WEEKLY ? schedule.daysOfWeek : [],
	dayOfMonth: frequency === ScheduleFrequency.MONTHLY ? (schedule.dayOfMonth ?? 1) : null,
})

const toggledDay = (days: number[], day: number, on: boolean) =>
	(on ? [...days, day] : days.filter((d) => d !== day)).sort((a, b) => a - b)

type ScheduleProps = { schedule: FileSchedule; errors: FieldErrors; onChange: (schedule: FileSchedule) => void }

function DaySelector({ schedule, errors, onChange }: ScheduleProps) {
	if (schedule.frequency === ScheduleFrequency.MONTHLY) {
		return (
			<NumberInput
				label="Day of month"
				path="schedule.dayOfMonth"
				errors={errors}
				value={schedule.dayOfMonth ?? 1}
				onChange={(dayOfMonth) => onChange({ ...schedule, dayOfMonth })}
			/>
		)
	}
	if (schedule.frequency !== ScheduleFrequency.WEEKLY) return null
	return (
		<fieldset className="flex flex-wrap gap-3">
			<legend className="text-ds-sm text-ds-text-muted">Days of week</legend>
			{WEEKDAYS.map(({ day, name }) => (
				<CheckboxInput
					key={day}
					label={name}
					checked={schedule.daysOfWeek.includes(day)}
					onChange={(on) => onChange({ ...schedule, daysOfWeek: toggledDay(schedule.daysOfWeek, day, on) })}
				/>
			))}
			<GroupError path="schedule.daysOfWeek" errors={errors} />
		</fieldset>
	)
}

function ScheduleWindow({ schedule, errors, onChange }: ScheduleProps) {
	const set = (patch: Partial<FileSchedule>) => onChange({ ...schedule, ...patch })
	return (
		<>
			<TextInput
				label="Timezone"
				path="schedule.timezone"
				errors={errors}
				value={schedule.timezone}
				onChange={(timezone) => set({ timezone })}
			/>
			<TextInput
				label="After"
				path="schedule.after"
				errors={errors}
				placeholder="HH:MM"
				value={schedule.after}
				onChange={(after) => set({ after })}
			/>
			<TextInput
				label="Expected before"
				path="schedule.expectedBefore"
				errors={errors}
				placeholder="HH:MM"
				value={schedule.expectedBefore}
				onChange={(expectedBefore) => set({ expectedBefore })}
			/>
			<NumberInput
				label="Missing grace period (minutes)"
				path="schedule.missingGracePeriodMinutes"
				errors={errors}
				value={schedule.missingGracePeriodMinutes}
				onChange={(missingGracePeriodMinutes) => set({ missingGracePeriodMinutes })}
			/>
			<Choice
				label="When missing"
				path="schedule.missingAction"
				errors={errors}
				value={schedule.missingAction}
				options={MISSING_OPTIONS}
				onChange={(missingAction) => set({ missingAction })}
			/>
		</>
	)
}

/**
 * When the file is expected (TO-391 spec Contract): off, or a frequency with
 * its day selector, a time window in a timezone, and what to do when the file
 * has not arrived after the grace period. Acted on by F2, stored here.
 */
export function ScheduleFields({
	schedule,
	errors,
	onChange,
}: {
	schedule: FileSchedule | null
	errors: FieldErrors
	onChange: (schedule: FileSchedule | null) => void
}) {
	return (
		<fieldset className="flex flex-col gap-3">
			<legend className="text-ds-sm font-semibold text-ds-text">When the file is expected</legend>
			<CheckboxInput
				label="Expect this file on a schedule"
				checked={schedule !== null}
				onChange={(on) => onChange(on ? { ...NEW_SCHEDULE } : null)}
			/>
			<GroupError path="schedule" errors={errors} />
			{schedule ? (
				<>
					<Choice
						label="Frequency"
						path="schedule.frequency"
						errors={errors}
						value={schedule.frequency}
						options={FREQUENCY_OPTIONS}
						onChange={(frequency) => onChange(withFrequency(schedule, frequency))}
					/>
					<DaySelector schedule={schedule} errors={errors} onChange={onChange} />
					<ScheduleWindow schedule={schedule} errors={errors} onChange={onChange} />
				</>
			) : null}
		</fieldset>
	)
}
